import { useState, useRef, useCallback } from "react";
import { Plus, Trash2, GripVertical, Image, Upload, Code2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import DOMPurify from "dompurify";

const API = process.env.REACT_APP_BACKEND_URL;

// ── DOMPurify config ────────────────────────────────────────────────────────
// Allows rich embeds (img / video / iframe) and basic formatting while
// blocking scripts, event handlers, and data-URI injections.
const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "b", "i", "u", "s", "strong", "em", "small", "span",
    "div", "section", "article", "main", "header", "footer", "nav",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "ul", "ol", "li",
    "blockquote", "pre", "code",
    "img", "figure", "figcaption",
    "video", "source",
    "iframe",
    "a",
    "table", "thead", "tbody", "tr", "th", "td",
    "hr",
    "style",
  ],
  ALLOWED_ATTR: [
    "href", "src", "alt", "title", "class", "id",
    "style", "width", "height",
    "target", "rel",
    "controls", "autoplay", "loop", "muted", "preload",
    "allowfullscreen", "allow", "frameborder", "scrolling", "referrerpolicy",
    "loading", "decoding",
    "colspan", "rowspan",
  ],
  ALLOW_DATA_ATTR: false,
  FORCE_BODY: true,
};

// ── CSS scoping ──────────────────────────────────────────────────────────────
// The container that wraps rendered product descriptions in ProductPage.js.
const SCOPE = ".raw-html-content";

/** Split a selector list on top-level commas only (ignores commas inside parens). */
function splitTopLevel(selectorText) {
  const parts = [];
  let depth = 0;
  let cur = "";
  for (const ch of selectorText) {
    if (ch === "(") depth++;
    else if (ch === ")") depth = Math.max(0, depth - 1);
    if (ch === "," && depth === 0) {
      parts.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  parts.push(cur);
  return parts;
}

/**
 * Prefix one parsed CSSRule's selectors with `scope` so the rule only fires
 * inside the description container.  Recursively handles @media / @supports.
 * Non-selector at-rules (@keyframes, @font-face, @charset …) are left intact.
 */
function prefixCssRule(rule, scope) {
  // CSSStyleRule (type 1) ─ normal selector { declarations }
  if (rule.type === 1) {
    const sel = splitTopLevel(rule.selectorText)
      .map(s => {
        s = s.trim();
        if (!s) return "";
        // :root / body / html → the scope div itself
        if (/^(:root|html|body)$/.test(s)) return scope;
        // "body .foo" → strip leading token, keep rest
        if (/^(html|body)\s/.test(s))
          return `${scope} ${s.replace(/^(html|body)\s+/, "")}`;
        // Already scoped (e.g. re-sanitizing saved HTML) → don't prefix twice
        if (s === scope || s.startsWith(`${scope} `) || s.startsWith(`${scope}.`)) return s;
        return `${scope} ${s}`;
      })
      .filter(Boolean)
      .join(", ");
    return `${sel} { ${rule.style.cssText} }`;
  }
  // CSSMediaRule (type 4) or CSSSupportsRule (type 12) ─ recurse into children
  if (rule.type === 4 || rule.type === 12) {
    const condition = rule.conditionText || rule.media?.mediaText || "";
    const keyword   = rule.type === 4 ? "@media" : "@supports";
    const inner = Array.from(rule.cssRules)
      .map(r => prefixCssRule(r, scope))
      .join("\n");
    return `${keyword} ${condition} {\n${inner}\n}`;
  }
  // Anything else (@keyframes, @font-face, @charset, @import …) ─ pass through
  return rule.cssText;
}

/**
 * Parse `css` with the browser's native CSS engine (no document-head side
 * effects — CSSStyleSheet constructor never attaches to the document) and
 * rewrite every selector to be scoped under `scope`.
 */
function scopeStylesheet(css, scope) {
  if (!css || !css.trim()) return css;
  try {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(css);
    const rules = Array.from(sheet.cssRules);
    // If the browser produced no rules (empty CSS, all @import, or any parse quirk)
    // return the original text so we never accidentally empty the <style> block.
    if (!rules.length) return css;
    const result = rules.map(r => prefixCssRule(r, scope)).join("\n");
    // Extra safety: if mapping somehow produced an empty string, keep original.
    return result || css;
  } catch {
    // Fallback: return the original CSS unscoped rather than break rendering
    return css;
  }
}

export function sanitizeHtml(html) {
  if (!html) return "";
  const clean = DOMPurify.sanitize(html, PURIFY_CONFIG);

  // If there are no <style> tags we're done
  if (!clean.includes("<style")) return clean;

  // Scope every <style> block so its rules only apply inside .raw-html-content
  const temp = document.createElement("div");
  temp.innerHTML = clean;
  temp.querySelectorAll("style").forEach(el => {
    el.textContent = scopeStylesheet(el.textContent, SCOPE);
  });
  return temp.innerHTML;
}

/**
 * Sanitize HTML (strip scripts + on* handlers) WITHOUT scoping <style> rules.
 * Use this for full-page HTML content (pages/landing pages) where the CSS
 * is meant to apply to the whole page, not just a container.
 */
export function sanitizeHtmlRaw(html) {
  if (!html) return "";
  return DOMPurify.sanitize(html, PURIFY_CONFIG);
}

// ── Toolbar button definitions ──────────────────────────────────────────────
const INLINE_ACTIONS = [
  { label: "B", title: "Bold",          cmd: "bold",         style: "font-bold" },
  { label: "I", title: "Italic",        cmd: "italic",       style: "italic" },
  { label: "U", title: "Underline",     cmd: "underline",    style: "underline" },
  { label: "S", title: "Strikethrough", cmd: "strikeThrough",style: "line-through" },
];

// Maps the select <option value> → formatBlock tag
const BLOCK_MAP = {
  paragraph: "p",
  h1: "h1",
  h2: "h2",
  h3: "h3",
};

export default function ContentTab({ data, onChange }) {
  const [activeBlock, setActiveBlock]   = useState("paragraph");
  const [showImgInput, setShowImgInput] = useState(false);
  const [imgUrl, setImgUrl]             = useState("");
  const [uploading, setUploading]       = useState(false);
  const [htmlMode, setHtmlMode]         = useState(false);
  const [editorKey, setEditorKey]       = useState(0); // bump to force remount

  const editorRef   = useRef(null);
  const fileInputRef = useRef(null);
  const savedRange  = useRef(null);

  // ── Save the selection so toolbar clicks don't lose it ──────────────────
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && editorRef.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  // ── Sync the block dropdown with wherever the cursor currently is ────────
  const updateBlockType = useCallback(() => {
    const sel = window.getSelection();
    if (!sel?.rangeCount) return;
    let node = sel.getRangeAt(0).commonAncestorContainer;
    while (node && node !== editorRef.current) {
      const tag = node.nodeName?.toLowerCase();
      if (tag === "h1" || tag === "h2" || tag === "h3") {
        setActiveBlock(tag);
        return;
      }
      node = node.parentNode;
    }
    setActiveBlock("paragraph");
  }, []);

  // ── Apply a block format (heading / paragraph) ───────────────────────────
  const applyBlock = (val) => {
    setActiveBlock(val);
    editorRef.current?.focus();
    document.execCommand("formatBlock", false, BLOCK_MAP[val] || "p");
  };

  // ── Inline format commands ────────────────────────────────────────────────
  const execCmd = (cmd) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, null);
  };

  // ── File upload (image / video) ──────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem("it_token");
      const form  = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/api/upload/media`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = `${API}${res.data.url}`;
      const html = file.type.startsWith("video/")
        ? `<video src="${url}" controls style="max-width:100%;height:auto;display:block;"></video><p><br></p>`
        : `<img src="${url}" style="max-width:100%;height:auto;display:block;" />`;
      editorRef.current?.focus();
      const sel = window.getSelection();
      if (savedRange.current) {
        sel.removeAllRanges();
        sel.addRange(savedRange.current);
      } else {
        const r = document.createRange();
        r.selectNodeContents(editorRef.current);
        r.collapse(false);
        sel.removeAllRanges();
        sel.addRange(r);
      }
      document.execCommand("insertHTML", false, html);
      onChange("product_description", sanitizeHtml(editorRef.current?.innerHTML || ""));
      toast.success(file.type.startsWith("video/") ? "Video added" : "Image added");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // ── Insert image by URL ──────────────────────────────────────────────────
  const insertImage = () => {
    const url = imgUrl.trim();
    if (!url) return;
    editorRef.current?.focus();
    document.execCommand("insertHTML", false, `<img src="${url}" style="max-width:100%;height:auto;display:block;" />`);
    onChange("product_description", sanitizeHtml(editorRef.current?.innerHTML || ""));
    setImgUrl("");
    setShowImgInput(false);
  };

  // ── Toggle HTML / Rich-text mode ─────────────────────────────────────────
  const toggleHtmlMode = () => {
    if (htmlMode) {
      // Leaving HTML mode → sanitize raw HTML the user typed
      const clean = sanitizeHtml(data.product_description || "");
      onChange("product_description", clean);
      setEditorKey(k => k + 1); // force contentEditable to remount with clean HTML
    }
    setHtmlMode(v => !v);
  };

  // ── Feature list helpers ─────────────────────────────────────────────────
  const features   = data.product_features || [];
  const addFeature = () => onChange("product_features", [...features, ""]);
  const updateFeature = (idx, val) => onChange("product_features", features.map((f, i) => i === idx ? val : f));
  const removeFeature = (idx) => onChange("product_features", features.filter((_, i) => i !== idx));

  // ── Toolbar shared button style ──────────────────────────────────────────
  const tbBtn = (active) =>
    `text-xs px-2 h-7 border rounded font-medium transition-colors flex items-center gap-1 ${
      active
        ? "border-orange-400 bg-orange-50 text-orange-600"
        : "border-slate-200 bg-white hover:bg-orange-50 hover:border-orange-300 text-slate-700"
    }`;

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <div className="w-5 h-5 bg-orange-100 rounded flex items-center justify-center">
          <span className="text-orange-600 text-xs font-bold">3</span>
        </div>
        <h2 className="text-base font-semibold text-slate-900">Content</h2>
      </div>

      {/* ── Product Description ──────────────────────────────────────────── */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-slate-800 mb-3">Product Description</label>

        {/* Toolbar */}
        <div className="rich-text-toolbar flex flex-wrap items-center gap-1 p-2 bg-slate-50 border border-slate-200 border-b-0 rounded-t-lg">

          {/* Block format dropdown — fixed: now calls execCommand('formatBlock') */}
          <select
            value={activeBlock}
            onChange={e => applyBlock(e.target.value)}
            disabled={htmlMode}
            className="text-xs border border-slate-200 rounded px-2 py-1 bg-white focus:outline-none focus:border-orange-500 disabled:opacity-40"
            title="Block format"
          >
            <option value="paragraph">Paragraph</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
          </select>

          <div className="w-px h-5 bg-slate-300 mx-1" />

          {/* Inline styles */}
          {INLINE_ACTIONS.map(a => (
            <button
              key={a.cmd}
              type="button"
              title={a.title}
              disabled={htmlMode}
              onMouseDown={e => { e.preventDefault(); execCmd(a.cmd); }}
              className={`text-xs font-${a.style} w-7 h-7 flex items-center justify-center rounded border border-slate-200 bg-white hover:bg-orange-50 hover:border-orange-300 disabled:opacity-40`}
            >
              {a.label}
            </button>
          ))}

          <div className="w-px h-5 bg-slate-300 mx-1" />

          <button type="button" disabled={htmlMode} onMouseDown={e => { e.preventDefault(); execCmd("insertUnorderedList"); }}
            className={`${tbBtn(false)} disabled:opacity-40`}>UL</button>
          <button type="button" disabled={htmlMode} onMouseDown={e => { e.preventDefault(); execCmd("insertOrderedList"); }}
            className={`${tbBtn(false)} disabled:opacity-40`}>OL</button>
          <button type="button" disabled={htmlMode} onMouseDown={e => { e.preventDefault(); execCmd("justifyLeft"); }}
            className={`${tbBtn(false)} disabled:opacity-40`}>≡</button>
          <button type="button" disabled={htmlMode} onMouseDown={e => { e.preventDefault(); execCmd("justifyCenter"); }}
            className={`${tbBtn(false)} disabled:opacity-40`}>☰</button>

          <div className="w-px h-5 bg-slate-300 mx-1" />

          {/* Insert image by URL */}
          <button
            type="button"
            title="Insert image by URL"
            disabled={htmlMode}
            onMouseDown={saveSelection}
            onClick={() => setShowImgInput(v => !v)}
            className={`${tbBtn(showImgInput)} disabled:opacity-40`}
          >
            <Image className="w-3.5 h-3.5" /> Image
          </button>

          {/* Upload image / video */}
          <button
            type="button"
            title="Upload image or video"
            data-testid="desc-upload-btn"
            disabled={uploading || htmlMode}
            onMouseDown={saveSelection}
            onClick={() => fileInputRef.current?.click()}
            className={`${tbBtn(false)} disabled:opacity-50`}
          >
            <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/mp4,video/webm,video/quicktime"
            className="hidden"
            onChange={handleFileUpload}
          />

          <div className="w-px h-5 bg-slate-300 mx-1" />

          {/* HTML source toggle */}
          <button
            type="button"
            title={htmlMode ? "Switch to rich-text editor" : "Edit raw HTML source"}
            onClick={toggleHtmlMode}
            data-testid="desc-html-toggle"
            className={`${tbBtn(htmlMode)} font-mono tracking-tight`}
          >
            <Code2 className="w-3.5 h-3.5" />
            {htmlMode ? "Rich Text" : "HTML"}
          </button>
        </div>

        {/* Image URL input row */}
        {showImgInput && !htmlMode && (
          <div className="flex gap-2 px-2 py-2 bg-orange-50 border border-orange-200 border-t-0 rounded-b-none">
            <input
              type="url"
              value={imgUrl}
              onChange={e => setImgUrl(e.target.value)}
              onKeyDown={e => e.key === "Enter" && insertImage()}
              placeholder="Paste image URL and press Enter or click Insert…"
              className="flex-1 text-xs px-3 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-orange-500 bg-white"
              autoFocus
            />
            <button
              type="button"
              onClick={insertImage}
              className="text-xs px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors"
            >
              Insert
            </button>
          </div>
        )}

        {/* ── Editor body ─────────────────────────────────────────────── */}
        {htmlMode ? (
          /* Raw HTML textarea */
          <textarea
            data-testid="desc-html-source"
            value={data.product_description || ""}
            onChange={e => onChange("product_description", e.target.value)}
            spellCheck={false}
            className="w-full border border-slate-200 rounded-b-lg px-3 py-3 text-sm font-mono leading-relaxed min-h-[300px] focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 resize-y bg-slate-900 text-green-300"
          />
        ) : (
          /* Rich-text contentEditable */
          <div
            key={editorKey}
            id="product-desc-editor"
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            className="rich-text-area"
            dangerouslySetInnerHTML={{ __html: data.product_description }}
            onBlur={e => onChange("product_description", sanitizeHtml(e.target.innerHTML))}
            onKeyUp={updateBlockType}
            onMouseUp={updateBlockType}
            onFocus={updateBlockType}
            data-placeholder="Enter detailed product description here..."
          />
        )}
      </div>

      {/* ── Product Features ─────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-800">Product Features</h3>
        </div>

        <div className="space-y-2">
          {features.map((feature, idx) => (
            <div key={idx} className="flex items-center gap-2 group">
              <div className="text-slate-300 cursor-grab flex-shrink-0">
                <GripVertical className="w-4 h-4" />
              </div>
              <input
                type="text"
                value={feature}
                onChange={e => updateFeature(idx, e.target.value)}
                placeholder={`Feature ${idx + 1}...`}
                className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 text-slate-900"
              />
              <button
                type="button"
                onClick={() => removeFeature(idx)}
                className="p-1.5 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all rounded-lg hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}

          {features.length === 0 && (
            <div className="text-center py-4 text-slate-400 text-sm border-2 border-dashed border-slate-200 rounded-lg">
              No features added yet. Click below to add features.
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={addFeature}
          className="mt-3 flex items-center gap-1.5 text-sm text-orange-600 hover:text-orange-700 font-medium"
        >
          <Plus className="w-4 h-4" />
          Add Feature
        </button>
      </div>
    </div>
  );
}
